import {logError} from "../services/logger.service";
import {enqueueSnackbar} from "notistack";

export interface LatLng {
  lat: number;
  lng: number;
}

export interface LocationInfo extends LatLng {
  /** The street alone ("8721 Brynwood Drive"): what the address field stores. */
  street: string;
  city: string;
  state: string;
  /** Five digits, never ZIP+4. */
  postalCode: string;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Whether a stored street already carries its city. Until SQ-502 the web
 * saved the whole formatted address in the street field ("8721 Brynwood
 * Drive, Boise, ID 83704") beside the geocoded city, state and zip. The city
 * must follow a comma: "123 Boise Ave" in Meridian is a street.
 */
export const streetCarriesCity = (street?: string | null, city?: string | null): boolean => {
  const s = street?.trim() ?? '';
  const c = city?.trim() ?? '';
  if(!s || !c) return false;
  return new RegExp(`,\\s*${escapeRegExp(c)}\\b`, 'i').test(s);
};

/**
 * An address on one line, "8721 Brynwood Drive, Boise, ID 83704": what the
 * address box shows, what page titles and cards read, and what is geocoded.
 * A street that still carries its city (an old record) is shown as it is.
 */
export const formatAddressLine = (
  street?: string | null,
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
): string => {
  // 'UNKNOWN' is the geocoder's placeholder for a part it did not find.
  const part = (value?: string | null) => {
    const trimmed = value?.trim() ?? '';
    return trimmed.toUpperCase() === 'UNKNOWN' ? '' : trimmed;
  };
  const trimmedStreet = part(street);
  if(streetCarriesCity(trimmedStreet, part(city))) return trimmedStreet;
  const stateZip = [part(state), part(postalCode).slice(0, 5)].filter(Boolean).join(' ');
  return [trimmedStreet, part(city), stateZip].filter(Boolean).join(', ');
};

const METERS_PER_DEGREE_LAT = 111_320;

/** Approximate distance in meters between two lat/lng points. */
export const distanceMeters = (a: LatLng, b: LatLng): number => {
  const midLatRad = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const dLat = (a.lat - b.lat) * METERS_PER_DEGREE_LAT;
  const dLng = (a.lng - b.lng) * METERS_PER_DEGREE_LAT * Math.cos(midLatRad);
  return Math.sqrt(dLat * dLat + dLng * dLng);
};

/** Offset a point by east/north meters (approximate, fine for short distances). */
export const offsetByMeters = (base: LatLng, eastMeters: number, northMeters: number): LatLng => {
  const latRad = base.lat * (Math.PI / 180);
  return {
    lat: base.lat + northMeters / METERS_PER_DEGREE_LAT,
    lng: base.lng + eastMeters / (METERS_PER_DEGREE_LAT * Math.cos(latRad)),
  };
};

/**
 * Pick a spawn point near `base` that is not already occupied.
 * Tries the base first, then a spiral of rings so back-to-back creates don't stack.
 */
export const nextOpenLocation = (
  base: LatLng,
  occupied: LatLng[],
  stepMeters = 3
): LatLng => {
  const isFree = (p: LatLng) =>
    !occupied.some(o => distanceMeters(o, p) < stepMeters * 0.8);

  if (isFree(base)) return base;

  for (let ring = 1; ring <= 10; ring++) {
    const count = ring * 6;
    for (let i = 0; i < count; i++) {
      const angle = (2 * Math.PI * i) / count;
      const candidate = offsetByMeters(
        base,
        Math.cos(angle) * stepMeters * ring,
        Math.sin(angle) * stepMeters * ring
      );
      if (isFree(candidate)) return candidate;
    }
  }
  return base;
};

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export const getLocationFromAddress = async (address: string): Promise<LocationInfo> => {
  try {
    let res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=AIzaSyBLw50vYSozTEviDIpu94K1KuwM4X_hDUM`);
    let data: any = await res.json();
    const firstResult = data.results[0];
    const location = firstResult.geometry.location;
    const city = firstResult.address_components.find((c: AddressComponent) => c.types.includes('locality'))?.long_name ?? 'UNKNOWN';
    const state = firstResult.address_components.find((c: AddressComponent) => c.types.includes('administrative_area_level_1'))?.short_name ?? 'UNKNOWN';
    // Five digits only (SQ-502): the ZIP+4 suffix is not wanted anywhere.
    const postalCode = firstResult.address_components.find((c: AddressComponent) => c.types.includes('postal_code'))?.long_name ?? 'UNKNOWN';
    // The street alone goes in the address field; city, state and zip have
    // their own. It used to store the whole line typed or picked, so every
    // reader that joined the fields showed city, state and zip twice (SQ-502).
    const streetNumber = firstResult.address_components.find((c: AddressComponent) => c.types.includes('street_number'))?.long_name;
    const route = firstResult.address_components.find((c: AddressComponent) => c.types.includes('route'))?.long_name;
    const street = route
      ? [streetNumber, route].filter(Boolean).join(' ')
      // No street in the result (a rural route, a PO box): what was typed
      // before the first comma.
      : address.split(',')[0].trim();
    return {
      lat: location.lat as number,
      lng: location.lng as number,
      street,
      city,
      state,
      postalCode,
    };
  } catch (e: any) {
    logError('Error getting location from address', {}, e)
    enqueueSnackbar(`Error getting location from address: ${e}`, {variant: 'error', autoHideDuration: 5000});
    throw e;
  }
}